export const REDACTED = '[REDACTED]'

/**
 * Secrets shorter than this are ignored. A short or empty token would match
 * everywhere and redact the whole bundle into uselessness.
 */
export const MIN_SECRET_LENGTH = 16

type Secret = string | null | undefined

/**
 * Builds a scrubber that removes known secret VALUES from a payload.
 *
 * The client half of the redaction split: it strips values it can name — the
 * app JWT, the OIDC id and refresh tokens — before the bundle ever leaves the
 * browser. Server-side pattern scrubbing is deliberately out of scope, but this
 * half could not move there anyway: the OIDC refresh token is opaque rather
 * than a JWT, so no pattern would ever match it, and scrubbing here means the
 * secret is never transmitted at all rather than removed at rest after passing
 * through CDN, WAF and proxy access logs.
 *
 * Build one of these PER SUBMISSION. A redactor constructed once at module load
 * reads its tokens before login, captures null forever, and ships every bundle
 * unredacted — a failure that stays invisible because tests asserting a known
 * token is scrubbed still pass.
 */
export function createRedactor(secrets: Secret[]): (value: unknown) => unknown {
  const usable = secrets.filter(
    (s): s is string => typeof s === 'string' && s.length >= MIN_SECRET_LENGTH,
  )

  const redactString = (input: string): string => {
    let out = input
    for (const secret of usable) {
      out = out.split(secret).join(REDACTED)
    }
    return out
  }

  const walk = (value: unknown, seen: WeakSet<object>): unknown => {
    if (typeof value === 'string') return redactString(value)
    if (value === null || typeof value !== 'object') return value

    // A cycle in a user-supplied structure must not take the submission down.
    if (seen.has(value)) return undefined
    seen.add(value)

    if (Array.isArray(value)) return value.map((item): unknown => walk(item, seen))

    const out: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value)) {
      out[key] = walk(item, seen)
    }
    return out
  }

  return (value: unknown): unknown => walk(value, new WeakSet<object>())
}
